/// <amd-module name="Wit/DeliveryPlans/Common/Components/WorkItemDependency" />
import { IObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { css, useComponentContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Callout, ContentSize } from "VSSUI/Callout";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Status, StatusSize, Statuses } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { IOrigin } from "VSSUI/Utilities/Position";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import {
    DependencyErrorType,
    WorkItemData,
    WorkItemDependencyData,
    WorkItemExtendedData
} from "Wit/DeliveryPlans/Common/Client/Contracts/DataProvider";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { getIconDataObservable } from "Wit/UI/Services/WorkItemTypeIconMetadataService";
import { TypeIconSize, WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import * as React from "react";
import { FieldRefNames } from "../Constants";
import { IDeliveryPlansIterationService } from "../Services/DeliveryPlansIterationService";
import { IDeliveryPlansWorkItemService } from "../Services/IDeliveryPlansWorkItemService";
import { getWorkItemUrl } from "../Utils/RoutingUtils";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";

export interface IWorkItemDependencyCalloutProps extends IWorkItemDependencyProps {
    onDismiss: () => void;
    anchorElement: HTMLElement;
    anchorOrigin: IOrigin;
    calloutOrigin: IOrigin;
}

export const WorkItemDependencyCallout: React.FunctionComponent<IWorkItemDependencyCalloutProps> = props => {
    const { anchorElement, anchorOrigin, calloutOrigin, onDismiss, ...rest } = props;
    return (
        <Callout
            anchorOrigin={anchorOrigin}
            anchorElement={anchorElement}
            blurDismiss
            calloutOrigin={calloutOrigin}
            contentClassName="flex-row flex-grow flex-center padding-8 subtle-border depth-8"
            contentSize={ContentSize.Large}
            escDismiss
            lightDismiss
            onDismiss={onDismiss}
        >
            <WorkItemDependency {...rest} />
        </Callout>
    );
};

export interface DependencyRow extends WorkItemDependencyData {
    expanded?: IObservableValue<boolean>;
}

export interface IWorkItemDependencyProps extends DependencyRow {
    onSaveWorkItem?: () => void;
    rowIndex?: number;
}

export const WorkItemDependency: React.FunctionComponent<IWorkItemDependencyProps> = ({
    dependencyError,
    expanded,
    isSourcePredecessor,
    onSaveWorkItem,
    rowIndex,
    sourceChain,
    source,
    targetChain,
    target
}) => {
    const { pageContext } = useComponentContext();
    const valid = dependencyError === DependencyErrorType.None;
    const innerDependency = (isExpanded: boolean) => (
        <>
            <div className="dependency-status flex-row padding-top-8">
                {expanded && (
                    <div className="margin-right-8">
                        <Button
                            iconProps={{ iconName: isExpanded ? "ChevronDown" : "ChevronRight", size: IconSize.small }}
                            onClick={e => {
                                e.preventDefault();
                                expanded.value = !expanded.value;
                            }}
                            subtle
                        />
                    </div>
                )}
                {!valid && (
                    <Status
                        {...Statuses.Warning}
                        ariaLabel={Resources.InvalidDependencyIconAriaLabel}
                        className="status-icon"
                        color="invalid-dependency"
                        size={StatusSize.l}
                    />
                )}
            </div>
            <WorkItemDisplay
                className="work-item-display"
                dependencyChain={sourceChain}
                direction="source"
                errorType={dependencyError}
                expanded={isExpanded}
                onSaveWorkItem={onSaveWorkItem}
                pageContext={pageContext}
                rowIndex={rowIndex}
                workItemDependencyData={source}
            />
            <div className="work-item-arrow flex-column flex-center padding-top-8">
                <Icon
                    className={css("work-item-dependency-arrow", valid ? "valid-dependency" : "invalid-dependency")}
                    iconName={isSourcePredecessor ? "Back" : "Forward"}
                    size={IconSize.large}
                    tooltipProps={{ text: isSourcePredecessor ? Resources.LeftDependencyArrowAriaLabel : Resources.RightDependencyArrowAriaLabel }}
                />
            </div>
            <WorkItemDisplay
                className="work-item-display"
                dependencyChain={targetChain}
                direction="target"
                errorType={dependencyError}
                expanded={isExpanded}
                onSaveWorkItem={onSaveWorkItem}
                pageContext={pageContext}
                rowIndex={rowIndex}
                workItemDependencyData={target}
            />
        </>
    );

    let dependency: JSX.Element;
    if (expanded) {
        dependency = <Observer expanded={expanded}>{(props: { expanded: boolean }) => innerDependency(props.expanded)}</Observer>;
    } else {
        dependency = innerDependency(true);
    }

    return <div className="work-item-dependency flex-grow flex-row padding-horizontal-20 padding-vertical-16">{dependency}</div>;
};

const WorkItemDisplay: React.FunctionComponent<{
    className: string;
    dependencyChain: WorkItemData[];
    direction: string;
    errorType: DependencyErrorType;
    expanded: boolean;
    onSaveWorkItem?: () => void;
    pageContext: IVssPageContext;
    rowIndex?: number;
    workItemDependencyData: WorkItemExtendedData;
}> = ({ className, dependencyChain, direction, errorType, expanded, onSaveWorkItem, pageContext, rowIndex, workItemDependencyData }) => {
    const { id, name, projectName, workItemType } = workItemDependencyData;
    if (!projectName || !workItemType) {
        return null;
    }

    const keyPrefix = rowIndex ? `dependency-${rowIndex}` : "dependency-";
    const colorAndIcon = getIconDataObservable(projectName, workItemType, pageContext);
    const url = getWorkItemUrl(pageContext, String(id), projectName);
    let title = <span className="body-xl text-ellipsis">{name}</span>;

    if (onSaveWorkItem) {
        title = (
            <Link
                className="flex-self-start margin-left-8 text-ellipsis"
                href={url}
                onClick={e => {
                    e.preventDefault();
                    const workItemService = pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
                    workItemService.openWorkItemDialog(id, onSaveWorkItem);
                }}
                tooltipProps={{ text: name }}
            >
                {title}
            </Link>
        );
    } else {
        title = (
            <div className="flex-self-start margin-left-8 text-ellipsis">
                <Tooltip overflowOnly>{title}</Tooltip>
            </div>
        );
    }

    return (
        <div className={css(className, "flex-column")}>
            <div className="flex-column rhythm-vertical-4">
                <div className="flex-row flex-center">
                    <WorkItemTypeIcon data={colorAndIcon} size={TypeIconSize.large} />
                    {title}
                </div>
                {renderWorkItemData(errorType, expanded, `${keyPrefix}-${direction}-`, pageContext, workItemDependencyData)}
            </div>
            {expanded
                ? dependencyChain?.map((item, index) => (
                      <ParentChainLink index={index} item={item} key={`${keyPrefix}-parent-${index}`} pageContext={pageContext} />
                  ))
                : undefined}
        </div>
    );
};

const ParentChainLink: React.FunctionComponent<{ item: WorkItemData; index: number; pageContext: IVssPageContext }> = ({
    item,
    index,
    pageContext
}) => {
    const colorAndIcon = getIconDataObservable(item.projectName, item.workItemType, pageContext);
    return (
        <div className="flex-row flex-end">
            <span className="flex-row flex-noshrink justify-end" style={{ width: `${(index + 1) * 32}px` }}>
                <svg className="curve" height="32px" width="32px">
                    <path d="M10 5 Q10 24 28 24" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
            </span>
            <WorkItemTypeIcon data={colorAndIcon} size={TypeIconSize.normal} />
            <Tooltip overflowOnly>
                <div className="text-ellipsis padding-left-8">{item.name}</div>
            </Tooltip>
        </div>
    );
};

export function renderWorkItemData(
    errorType: DependencyErrorType,
    expanded: boolean,
    keyPrefix: string,
    pageContext: IVssPageContext,
    workItemData: WorkItemExtendedData
): JSX.Element[] | null {
    const fieldRows: JSX.Element[] = [];
    if (workItemData.targetDate) {
        let fieldService : IWorkItemFieldService | IWorkItemFieldService2;
        if (isFeatureFlagEnabled(pageContext, FeatureFlags.CardsUseNewWorkItemFieldService, false)) {
            fieldService = pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
        } else {
            fieldService = pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
        }
        const field = fieldService.getFieldByReferenceName(FieldRefNames.TargetDate);
        const fieldValue = workItemData.targetDate;

        if (field && fieldValue) {
            fieldRows.push(
                getFieldRow(
                    errorType === DependencyErrorType.TargetDate ? "invalid-dependency" : "",
                    field.name,
                    fieldValue.toLocaleDateString(undefined, { year: "numeric", month: "numeric", day: "numeric" }),
                    keyPrefix + field.name
                )
            );
        }
    }

    if (workItemData.iterationId && (expanded || errorType === DependencyErrorType.Iteration || !workItemData.targetDate)) {
        const iterationService = pageContext.getService<IDeliveryPlansIterationService>("IDeliveryPlansIterationService");
        const iteration = iterationService.getIterationById(workItemData.iterationId);

        if (iteration) {
            fieldRows.push(getFieldRow("", Resources.IterationName, iteration.name, keyPrefix + iteration.name));

            const iterationEndFieldName = Resources.IterationEndDate;
            const iterationEndFieldValue = iteration.endDate;
            if (iterationEndFieldValue) {
                fieldRows.push(
                    getFieldRow(
                        errorType !== DependencyErrorType.None && !workItemData.targetDate ? "invalid-dependency" : "",
                        iterationEndFieldName,
                        iterationEndFieldValue.toLocaleDateString(undefined, { year: "numeric", month: "numeric", day: "numeric" }),
                        keyPrefix + iterationEndFieldName
                    )
                );
            }
        }
    }

    if (expanded) {
        fieldRows.push(getFieldRow("", Resources.ProjectName, workItemData.projectName, keyPrefix + Resources.ProjectName));
    }

    return fieldRows;
}

export function getFieldRow(className: string, fieldName: string, fieldValue: string, key: string) {
    return (
        <div className="flex-row" key={key}>
            <span className="padding-right-4 secondary-text label">{fieldName + ": "}</span>
            <span className={css("value", className)}>{fieldValue}</span>
        </div>
    );
}
